// FILE: src/admin/pages/compliance/drivers/AdminDriverComplianceDetail.jsx
// ✅ Mirrors vehicle compliance detail UX
// ✅ Docs + Photos cards with Open/Download
// ✅ Correction + Unlock modals
// ✅ Recompute / Request Correction / Unlock / Mark reviewed / Override approve
// ✅ Removed Cancel button/action
// ✅ Uses SAME dropdown pill style (like your screenshot)

import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  approveDriverCompliance,
  forceVerifyDriverCompliance,
  getDriverComplianceDetail,
  requestDriverComplianceCorrection,
  recomputeDriverCompliance,
  overrideApproveDriverCompliance,
  unlockReuploadDriverCompliance,
  openAdminDriverFile,
  downloadAdminDriverFile,
} from "../../../../api/adminDriverComplianceApi";

const DOCS = [
  { key: "DRIVING_LICENSE", label: "DRIVING_LICENSE", docType: "DRIVING_LICENSE", docSide: "NA" },
  { key: "AADHAAR_FRONT", label: "AADHAAR (FRONT)", docType: "AADHAAR", docSide: "FRONT" },
  { key: "AADHAAR_BACK", label: "AADHAAR (BACK)", docType: "AADHAAR", docSide: "BACK" },
  { key: "CHARACTER_CERTIFICATE", label: "CHARACTER_CERTIFICATE", docType: "CHARACTER_CERTIFICATE", docSide: "NA" },
];

const PHOTO_ORDER = ["PROFILE"];

/* =============================================================================
   SMALL HELPERS
============================================================================= */
function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}
function safeStr(x) {
  return x == null ? "" : String(x);
}
function up(x) {
  return safeStr(x).trim().toUpperCase();
}

/* =============================================================================
   UI ATOMS
============================================================================= */
function Tag({ label, tone = "neutral", className = "" }) {
  const cls =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : tone === "warn"
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : tone === "bad"
      ? "border-rose-200 bg-rose-50 text-rose-800"
      : "border-slate-200 bg-slate-50 text-slate-700";

  return (
    <span
      className={classNames(
        "inline-flex items-center border px-2 py-1 text-[11px] font-bold uppercase tracking-wider leading-none rounded-md",
        cls,
        className
      )}
    >
      {label}
    </span>
  );
}

function StatusTag({ status }) {
  const s = up(status);
  const tone =
    s === "APPROVED_VERIFIED"
      ? "good"
      : s === "PENDING_ADMIN_REVIEW"
      ? "warn"
      : s === "ACTION_REQUIRED"
      ? "bad"
      : "neutral";
  return <Tag label={s || "—"} tone={tone} />;
}

function Button({
  children,
  onClick,
  disabled,
  tone = "soft",
  className = "",
  title,
  type = "button",
}) {
  const cls =
    tone === "danger"
      ? "border-rose-700 bg-rose-700 text-white hover:opacity-95"
      : tone === "dark"
      ? "border-slate-900 bg-slate-900 text-white hover:opacity-95"
      : tone === "ghost"
      ? "border-transparent bg-transparent text-slate-700 hover:bg-slate-50"
      : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50";

  return (
    <button
      type={type}
      title={title}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={classNames(
        "inline-flex h-10 items-center justify-center gap-2 border px-4 text-sm font-semibold rounded-xl shadow-sm",
        "disabled:opacity-60 disabled:cursor-not-allowed",
        cls,
        className
      )}
    >
      {children}
    </button>
  );
}

/**
 * ✅ Dropdown style that matches your screenshot:
 * - pill select
 * - caret ▼
 * - compact + premium
 */
function SelectPill({ value, onChange, children, className = "", disabled = false }) {
  return (
    <div className={classNames("relative", className)}>
      <select
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={classNames(
          "h-10 w-full appearance-none rounded-xl border border-slate-200 bg-white",
          "px-4 pr-10 text-sm font-semibold text-slate-900 shadow-sm",
          "hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-200",
          "disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
        )}
      >
        {children}
      </select>

      <svg
        viewBox="0 0 24 24"
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M6 9l6 6 6-6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}

function Section({ title, subtitle, right, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">
            {title}
          </div>
          {subtitle ? (
            <div className="mt-1 text-[13px] font-medium text-slate-500">{subtitle}</div>
          ) : null}
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

function KV({ k, v, hint, right }) {
  return (
    <div className="grid grid-cols-12 gap-3 py-3">
      <div className="col-span-12 md:col-span-4 text-[12px] font-bold uppercase tracking-wider text-slate-500">
        {k}
      </div>
      <div className="col-span-12 md:col-span-8 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="break-words text-sm font-semibold text-slate-900">{v ?? "—"}</div>
          {hint ? <div className="mt-1 text-[12px] font-medium text-slate-500">{hint}</div> : null}
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </div>
    </div>
  );
}

function ModalShell({ title, subtitle, onClose, children, footer }) {
  return (
    <div className="fixed inset-0 z-[60]">
      <div className="absolute inset-0 bg-black/35" onClick={onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl rounded-xl border border-slate-200 bg-white shadow-xl">
          <div className="border-b border-slate-200 px-5 py-4">
            <div className="text-lg font-extrabold tracking-tight text-slate-900">{title}</div>
            {subtitle ? <div className="mt-1 text-[13px] font-medium text-slate-500">{subtitle}</div> : null}
          </div>
          <div className="px-5 py-4">{children}</div>
          {footer ? <div className="border-t border-slate-200 px-5 py-4">{footer}</div> : null}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] font-bold text-slate-500">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/* =============================================================================
   DTO LOOKUPS
============================================================================= */
/** DTO: { docs: Map<string, DocItem>, photos: Map<string, PhotoItem> } */
function findDoc(docsMap, docType, docSide) {
  const docs = docsMap || {};
  const t = up(docType);
  const s = up(docSide || "NA");

  const candidates = [
    `${t}_${s}`,
    `${t}:${s}`,
    `${t}|${s}`,
    `${t}`,
    s === "NA" ? `${t}_NA` : null,
  ].filter(Boolean);

  for (const k of candidates) {
    if (docs[k]) return docs[k];
  }

  const vals = Object.values(docs);
  const hit = vals.find((x) => up(x?.docType) === t && up(x?.docSide || "NA") === s);
  return hit || null;
}

function findPhoto(photosMap, photoType) {
  const photos = photosMap || {};
  const t = up(photoType);
  if (photos[t]) return photos[t];
  const vals = Object.values(photos);
  return vals.find((x) => up(x?.photoType) === t) || null;
}

function pickFileRef(it) {
  return it?.fileKey || it?.s3Key || it?.key || it?.fileUrl || it?.url || "";
}

function toneForDocState(state) {
  const s = up(state);
  if (s === "VERIFIED") return "good";
  if (s === "UPLOADED" || s === "PENDING_REVIEW") return "warn";
  if (s === "ACTION_REQUIRED" || s === "MISSING") return "bad";
  return "neutral";
}

function toneForIssue(issue) {
  const s = up(issue);
  if (!s || s === "NONE") return "neutral";
  if (s === "EXPIRED" || s === "REG_MISMATCH" || s === "INVALID_FORMAT") return "bad";
  return "warn";
}

/* =============================================================================
   CARDS
============================================================================= */
function DocCard({ label, it }) {
  const state = up(it?.state || "MISSING");
  const issueCode = up(it?.issueCode || "");
  const msg = safeStr(it?.issueMessage || "").trim();

  const fileRef = pickFileRef(it);
  const hasFile = !!safeStr(fileRef).trim();
  const [busy, setBusy] = useState(false);

  const onOpen = async () => {
    if (!hasFile) return;
    try {
      setBusy(true);
      await openAdminDriverFile(fileRef);
    } catch (e) {
      alert(e?.message || "Failed to open file");
    } finally {
      setBusy(false);
    }
  };

  const onDownload = async () => {
    if (!hasFile) return;
    try {
      setBusy(true);
      await downloadAdminDriverFile(fileRef);
    } catch (e) {
      alert(e?.message || "Failed to download file");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-sm font-extrabold text-slate-900">{label}</div>
            <Tag label={state || "—"} tone={toneForDocState(state)} />
            {issueCode ? <Tag label={issueCode} tone={toneForIssue(issueCode)} /> : null}
          </div>

          {msg ? <div className="mt-2 text-[13px] font-medium text-slate-700">{msg}</div> : null}
          {!hasFile ? <div className="mt-2 text-[13px] font-medium text-slate-500">No file attached.</div> : null}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Button disabled={!hasFile || busy} onClick={onOpen} title={!hasFile ? "No file" : "Open file"}>
            {busy ? "…" : "Open"}
          </Button>
          <Button
            disabled={!hasFile || busy}
            onClick={onDownload}
            title={!hasFile ? "No file" : "Download file"}
          >
            {busy ? "…" : "Download"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PhotoCard({ label, it }) {
  const state = up(it?.state || "MISSING");
  const fileRef = pickFileRef(it);
  const hasFile = !!safeStr(fileRef).trim();
  const [busy, setBusy] = useState(false);

  const tone = state === "VERIFIED" ? "good" : state === "UPLOADED" ? "warn" : state === "MISSING" ? "bad" : "neutral";

  const onOpen = async () => {
    if (!hasFile) return;
    try {
      setBusy(true);
      await openAdminDriverFile(fileRef);
    } catch (e) {
      alert(e?.message || "Failed to open file");
    } finally {
      setBusy(false);
    }
  };

  const onDownload = async () => {
    if (!hasFile) return;
    try {
      setBusy(true);
      await downloadAdminDriverFile(fileRef);
    } catch (e) {
      alert(e?.message || "Failed to download file");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-sm font-extrabold text-slate-900">{label}</div>
            <Tag label={state || "—"} tone={tone} />
          </div>
          {!hasFile ? <div className="mt-2 text-[13px] font-medium text-slate-500">No file attached.</div> : null}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Button disabled={!hasFile || busy} onClick={onOpen} title={!hasFile ? "No file" : "Open file"}>
            {busy ? "…" : "Open"}
          </Button>
          <Button
            disabled={!hasFile || busy}
            onClick={onDownload}
            title={!hasFile ? "No file" : "Download file"}
          >
            {busy ? "…" : "Download"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* =============================================================================
   PAGE
============================================================================= */
export default function AdminDriverComplianceDetail() {
  const { driverId } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [reason, setReason] = useState("");

  const [corrOpen, setCorrOpen] = useState(false);
  const [corr, setCorr] = useState({
    targetType: "DOC",
    docType: "DRIVING_LICENSE",
    docSide: "NA",
    photoType: "PROFILE",
    issueCode: "OTHER",
    message: "",
    clearFile: false,
  });

  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlock, setUnlock] = useState({
    targetType: "DOC",
    docType: "DRIVING_LICENSE",
    docSide: "NA",
    photoType: "PROFILE",
    clearFile: true,
    message: "Please re-upload a clear and correct file.",
  });

  const handle401 = (e) => {
    const status = e?.status || e?.response?.status;
    if (status === 401) {
      alert("Your admin session expired. Please login again.");
      return true;
    }
    return false;
  };

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const res = await getDriverComplianceDetail(driverId);
      setData(res);
    } catch (e) {
      if (handle401(e)) setErr("Session expired. Please login again.");
      else setErr(e?.message || "Failed to load compliance");
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  async function refreshAfterAction() {
    try {
      await load();
    } catch (e) {
      const status = e?.status || e?.response?.status;
      if (status === 401) setErr("Session expired. Please login again.");
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverId]);

  const dto = data?.compliance || null;
  const docsMap = useMemo(() => dto?.docs || {}, [dto]);
  const photosMap = useMemo(() => dto?.photos || {}, [dto]);

  const isVerified = Boolean(dto?.complianceVerified);
  const isComplete = Boolean(dto?.complianceComplete);
  const canOverrideApprove = isComplete && !isVerified;
  const canForceVerify = !isVerified;

  const doMarkReviewed = async () => {
    if (!window.confirm("Mark as reviewed (audit only)?")) return;
    try {
      await approveDriverCompliance(driverId, { reason });
    } catch (e) {
      if (!handle401(e)) alert(e?.message || "Failed");
      return;
    }
    await refreshAfterAction();
  };

  const doRecompute = async () => {
    try {
      await recomputeDriverCompliance(driverId);
    } catch (e) {
      if (!handle401(e)) alert(e?.message || "Failed to recompute");
      return;
    }
    await refreshAfterAction();
  };

  const doOverrideApprove = async () => {
    if (!window.confirm("Override approve (manual verify)? Use only when documents are authentic.")) return;
    try {
      await overrideApproveDriverCompliance(driverId, { reason });
    } catch (e) {
      if (!handle401(e)) alert(e?.message || "Override approve failed");
      return;
    }
    await refreshAfterAction();
  };

  const doForceVerify = async () => {
    if (!window.confirm("Force verify this driver? This bypasses document checks and admin restrictions.")) return;
    try {
      await forceVerifyDriverCompliance(driverId, { reason });
    } catch (e) {
      if (!handle401(e)) alert(e?.message || "Force verify failed");
      return;
    }
    await refreshAfterAction();
  };

  const doRequestCorrection = async () => {
    const payload = { ...corr };
    payload.targetType = up(payload.targetType) === "PHOTO" ? "PHOTO" : "DOC";

    if (payload.targetType === "DOC") {
      payload.docType = up(payload.docType);
      payload.docSide = up(payload.docSide || "NA");
      payload.issueCode = up(payload.issueCode || "OTHER");
      payload.clearFile = Boolean(payload.clearFile);
      delete payload.photoType;
    } else {
      payload.photoType = up(payload.photoType);
      payload.issueCode = up(payload.issueCode || "OTHER");
      payload.clearFile = payload.clearFile !== false;
      delete payload.docType;
      delete payload.docSide;
    }

    if (typeof payload.message === "string") payload.message = payload.message.trim();

    try {
      await requestDriverComplianceCorrection(driverId, payload);
      setCorrOpen(false);
    } catch (e) {
      if (!handle401(e)) alert(e?.message || "Failed to request correction");
      return;
    }
    await refreshAfterAction();
  };

  const doUnlock = async () => {
    const payload = { ...unlock };
    payload.targetType = up(payload.targetType) === "PHOTO" ? "PHOTO" : "DOC";

    if (payload.targetType === "DOC") {
      payload.docType = up(payload.docType);
      payload.docSide = up(payload.docSide || "NA");
      payload.clearFile = Boolean(payload.clearFile);
      delete payload.photoType;
    } else {
      payload.photoType = up(payload.photoType);
      payload.clearFile = Boolean(payload.clearFile);
      delete payload.docType;
      delete payload.docSide;
    }

    if (typeof payload.message === "string") payload.message = payload.message.trim();

    try {
      await unlockReuploadDriverCompliance(driverId, payload);
      setUnlockOpen(false);
    } catch (e) {
      if (!handle401(e)) alert(e?.message || "Failed to unlock reupload");
      return;
    }
    await refreshAfterAction();
  };

  return (
    <div className="min-w-0 space-y-4">
      <Section
        title="Compliance detail"
        subtitle="Review docs/photos, request correction, unlock reupload, or verify."
        right={
          <div className="flex flex-wrap items-center gap-2 justify-end">
            <StatusTag status={dto?.status} />
            <Tag label={isComplete ? "COMPLETE" : "INCOMPLETE"} tone={isComplete ? "good" : "neutral"} />
            <Tag
              label={isVerified ? "VERIFIED" : "NOT VERIFIED"}
              tone={isVerified ? "good" : dto?.status === "PENDING_ADMIN_REVIEW" ? "warn" : "neutral"}
            />
          </div>
        }
      >
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="text-2xl font-extrabold tracking-tight text-slate-900">Driver #{driverId}</div>
            <div className="mt-1 text-[13px] font-medium text-slate-500">
              Keep actions minimal here; use Support/Ops for other workflows.
            </div>

            {err ? (
              <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-semibold text-rose-700">
                {err}
              </div>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-2 justify-start lg:justify-end">
            <Button onClick={doRecompute} disabled={loading} title="Recompute compliance from stored files">
              Recompute
            </Button>
            <Button
              onClick={() => setUnlockOpen(true)}
              disabled={loading}
              title="Allow driver to reupload (optional clear file)"
            >
              Unlock reupload
            </Button>
            <Button onClick={() => setCorrOpen(true)} disabled={loading} title="Ask driver to correct a specific doc/photo">
              Request correction
            </Button>
            <Button onClick={doMarkReviewed} disabled={loading} title="Audit only (does not verify)">
              Mark reviewed
            </Button>

            <Button
              onClick={doOverrideApprove}
              disabled={loading || !canOverrideApprove}
              tone="dark"
              title={!canOverrideApprove ? "Requires complianceComplete=true and not already verified" : "Manual verify"}
            >
              Override approve
            </Button>
            <Button
              onClick={doForceVerify}
              disabled={loading || !canForceVerify}
              tone="danger"
              title={!canForceVerify ? "Driver is already verified" : "Bypass document checks and force verification"}
            >
              Force verify
            </Button>
          </div>
        </div>
      </Section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Section title="Driver" subtitle="Identity & ownership.">
          <KV k="Name" v={data?.driver?.name || "—"} />
          <KV k="Phone" v={data?.driver?.phone || "—"} />
          <KV k="Email" v={data?.driver?.email || "—"} />
          <KV k="OwnerId" v={data?.driver?.ownerId != null ? String(data.driver.ownerId) : "—"} />
        </Section>

        <Section title="Compliance" subtitle="High-signal fields only.">
          <KV
            k="Status"
            v={
              <div className="flex flex-wrap items-center gap-2">
                <StatusTag status={dto?.status} />
              </div>
            }
          />
          <KV k="Complete" v={<Tag label={isComplete ? "YES" : "NO"} tone={isComplete ? "good" : "neutral"} />} />
          <KV
            k="Verified"
            v={
              <Tag
                label={isVerified ? "YES" : "NO"}
                tone={isVerified ? "good" : dto?.status === "PENDING_ADMIN_REVIEW" ? "warn" : "neutral"}
              />
            }
          />
          {dto?.message ? <KV k="Message" v={dto.message} /> : null}
        </Section>

        <Section title="Admin note" subtitle="Optional reason text for actions.">
          <div className="text-[11px] font-bold text-slate-500">Reason</div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="mt-1 w-full resize-none rounded-xl border border-slate-200 bg-white p-3 text-sm font-medium text-slate-900 outline-none placeholder:text-slate-400 focus:border-slate-300"
            rows={6}
            placeholder="Short reason / note for audit log..."
          />
        </Section>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section title="Documents" subtitle="State + issue + short message. Actions on right.">
          {loading ? (
            <div className="text-sm font-semibold text-slate-600">Loading…</div>
          ) : (
            <div className="space-y-2">
              {DOCS.map((d) => {
                const it = findDoc(docsMap, d.docType, d.docSide);
                return <DocCard key={d.key} label={d.label} it={it} />;
              })}
            </div>
          )}
        </Section>

        <Section title="Photos" subtitle="Profile photo. Keep it quick.">
          {loading ? (
            <div className="text-sm font-semibold text-slate-600">Loading…</div>
          ) : (
            <div className="space-y-2">
              {PHOTO_ORDER.map((k) => {
                const it = findPhoto(photosMap, k);
                return <PhotoCard key={k} label={k} it={it} />;
              })}
            </div>
          )}
        </Section>
      </div>

      {/* ========================= MODALS ========================= */}
      {corrOpen ? (
        <ModalShell
          title="Request correction"
          subtitle="Pick one target and send a short, clear message."
          onClose={() => setCorrOpen(false)}
          footer={
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button onClick={() => setCorrOpen(false)}>Cancel</Button>
              <Button tone="dark" onClick={doRequestCorrection}>
                Send request
              </Button>
            </div>
          }
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Target">
                <SelectPill
                  value={corr.targetType}
                  onChange={(e) => setCorr((p) => ({ ...p, targetType: e.target.value }))}
                >
                  <option value="DOC">Document</option>
                  <option value="PHOTO">Photo</option>
                </SelectPill>
              </Field>

              {corr.targetType === "DOC" ? (
                <Field label="Document type">
                  <SelectPill
                    value={corr.docType}
                    onChange={(e) => {
                      const next = e.target.value;
                      setCorr((p) => ({
                        ...p,
                        docType: next,
                        docSide: next === "AADHAAR" ? "FRONT" : "NA",
                      }));
                    }}
                  >
                    <option value="DRIVING_LICENSE">DRIVING_LICENSE</option>
                    <option value="AADHAAR">AADHAAR</option>
                    <option value="CHARACTER_CERTIFICATE">CHARACTER_CERTIFICATE</option>
                  </SelectPill>
                </Field>
              ) : (
                <Field label="Photo type">
                  <SelectPill
                    value={corr.photoType}
                    onChange={(e) => setCorr((p) => ({ ...p, photoType: e.target.value }))}
                  >
                    <option value="PROFILE">PROFILE</option>
                  </SelectPill>
                </Field>
              )}
            </div>

            {corr.targetType === "DOC" && up(corr.docType) === "AADHAAR" ? (
              <Field label="Doc side">
                <SelectPill value={corr.docSide} onChange={(e) => setCorr((p) => ({ ...p, docSide: e.target.value }))}>
                  <option value="FRONT">FRONT</option>
                  <option value="BACK">BACK</option>
                </SelectPill>
              </Field>
            ) : null}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Issue code">
                <SelectPill value={corr.issueCode} onChange={(e) => setCorr((p) => ({ ...p, issueCode: e.target.value }))}>
                  <option value="MISSING_FILE">MISSING_FILE</option>
                  <option value="UNREADABLE">UNREADABLE</option>
                  <option value="EXPIRED">EXPIRED</option>
                  <option value="INVALID_FORMAT">INVALID_FORMAT</option>
                  <option value="REG_MISMATCH">REG_MISMATCH</option>
                  <option value="OTHER">OTHER</option>
                </SelectPill>
              </Field>

              <Field label="Clear file">
                <SelectPill
                  value={String(Boolean(corr.clearFile))}
                  onChange={(e) => setCorr((p) => ({ ...p, clearFile: e.target.value === "true" }))}
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </SelectPill>
              </Field>
            </div>

            <Field label="Message to driver (short & clear)">
              <textarea
                value={corr.message}
                onChange={(e) => setCorr((p) => ({ ...p, message: e.target.value }))}
                className="w-full resize-none rounded-xl border border-slate-200 bg-white p-3 text-sm font-medium text-slate-900 outline-none placeholder:text-slate-400 focus:border-slate-300"
                rows={3}
                placeholder="Example: DL is unreadable. Please upload a clearer Driving License."
              />
            </Field>
          </div>
        </ModalShell>
      ) : null}

      {unlockOpen ? (
        <ModalShell
          title="Unlock reupload"
          subtitle="Allow driver to upload again (optionally clear current file)."
          onClose={() => setUnlockOpen(false)}
          footer={
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button onClick={() => setUnlockOpen(false)}>Cancel</Button>
              <Button tone="dark" onClick={doUnlock}>
                Unlock
              </Button>
            </div>
          }
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Target">
                <SelectPill
                  value={unlock.targetType}
                  onChange={(e) => setUnlock((p) => ({ ...p, targetType: e.target.value }))}
                >
                  <option value="DOC">Document</option>
                  <option value="PHOTO">Photo</option>
                </SelectPill>
              </Field>

              {unlock.targetType === "DOC" ? (
                <Field label="Document type">
                  <SelectPill
                    value={unlock.docType}
                    onChange={(e) => {
                      const next = e.target.value;
                      setUnlock((p) => ({
                        ...p,
                        docType: next,
                        docSide: next === "AADHAAR" ? "FRONT" : "NA",
                      }));
                    }}
                  >
                    <option value="DRIVING_LICENSE">DRIVING_LICENSE</option>
                    <option value="AADHAAR">AADHAAR</option>
                    <option value="CHARACTER_CERTIFICATE">CHARACTER_CERTIFICATE</option>
                  </SelectPill>
                </Field>
              ) : (
                <Field label="Photo type">
                  <SelectPill
                    value={unlock.photoType}
                    onChange={(e) => setUnlock((p) => ({ ...p, photoType: e.target.value }))}
                  >
                    <option value="PROFILE">PROFILE</option>
                  </SelectPill>
                </Field>
              )}
            </div>

            {unlock.targetType === "DOC" && up(unlock.docType) === "AADHAAR" ? (
              <Field label="Doc side">
                <SelectPill
                  value={unlock.docSide}
                  onChange={(e) => setUnlock((p) => ({ ...p, docSide: e.target.value }))}
                >
                  <option value="FRONT">FRONT</option>
                  <option value="BACK">BACK</option>
                </SelectPill>
              </Field>
            ) : null}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Clear file">
                <SelectPill
                  value={String(Boolean(unlock.clearFile))}
                  onChange={(e) => setUnlock((p) => ({ ...p, clearFile: e.target.value === "true" }))}
                >
                  <option value="true">Yes (recommended)</option>
                  <option value="false">No</option>
                </SelectPill>
              </Field>
            </div>

            <Field label="Message to driver">
              <textarea
                value={unlock.message}
                onChange={(e) => setUnlock((p) => ({ ...p, message: e.target.value }))}
                className="w-full resize-none rounded-xl border border-slate-200 bg-white p-3 text-sm font-medium text-slate-900 outline-none placeholder:text-slate-400 focus:border-slate-300"
                rows={3}
              />
            </Field>
          </div>
        </ModalShell>
      ) : null}
    </div>
  );
}
